import type { SorterResult } from "antd/lib/table/interface";
import { type FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import type { AcquirerList } from "src/app/features/Acquirer/types/AcquirerList";
import { type ExtendedColumnsType, GenericTable, type ResourceTableProps } from "../../../../../components";
import {
    displayDateTimeValue,
    displayStatusValue,
} from "../../../../../components/Table/components/PropertyTable/transformations/cellTransformations";
import { ResourceName, ResourceVariant } from "../../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../../core/router/types/routerTypes";

export const AcquirersTable: FC<ResourceTableProps<AcquirerList>> = ({ onStateChanged, onRowSelectionChange, datasource, resource }) => {
    const { t } = useTranslation();
    const getColumnsConfig = useCallback(
        (sortInfo: SorterResult<AcquirerList>): ExtendedColumnsType<AcquirerList>[] => {
            return [
                {
                    title: t("acquirerList.id"),
                    key: "id",
                    dataIndex: "id",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "id" ? sortInfo.order : null,
                },
                {
                    title: t("acquirerList.name"),
                    key: "name",
                    dataIndex: "name",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "name" ? sortInfo.order : null,
                },
                {
                    title: t("acquirerList.numberOfSites"),
                    key: "numberOfSites",
                    dataIndex: "numberOfSites",
                    sorter: (a, b) => a.numberOfSites! - b.numberOfSites!,
                    sortOrder: sortInfo.columnKey === "numberOfSites" ? sortInfo.order : null,
                },
                {
                    title: t("acquirerList.status"),
                    key: "status",
                    dataIndex: "status",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "status" ? sortInfo.order : null,
                    render: (value) => displayStatusValue(value),
                },
                {
                    title: t("acquirerList.changed"),
                    dataIndex: "changed",
                    key: "changed",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "changed" ? sortInfo.order : null,
                    render: (value) => displayDateTimeValue(value),
                },
            ];
        },
        [t],
    );

    const navigate = useNavigate();
    const onRowClick = (record: AcquirerList) => {
        const route = record.physical
            ? `${BASE_PATH}/${ResourceName.ACQUIRER}/${ResourceVariant.PHYSICAL}/${FORM_ACTION.EDIT}/${record.id}?acquirerId=${record.id}`
            : `${BASE_PATH}/${ResourceName.ACQUIRER}/${ResourceVariant.VIRTUAL}/${FORM_ACTION.EDIT}/${record.id}`;
        navigate(route);
    };

    return (
        (datasource && (
            <GenericTable<AcquirerList>
                getColumnsConfig={getColumnsConfig}
                datasource={datasource}
                name="acquirers_table"
                onSelectionCallback={onRowSelectionChange}
                onStateChanged={onStateChanged}
                onRow={(record: AcquirerList) => {
                    return {
                        onClick: () => onRowClick(record),
                    };
                }}
                resource={resource}
            />
        )) ||
        null
    );
};
